import React, { useEffect, useState, useRef } from "react";
import {
  Grid,
  Header,
  Container,
  Segment,
  Button,
  Icon,
  Message,
} from "semantic-ui-react";
import { sessionManager } from "@authentication/services/SessionManager";
import PostModal from "./modals/PostModals";
import { announcementApi } from "@api/announcement";
import Post from "./component/Post";
import TopBorrowedBooks from "./component/TopBorrowedBooks";
import TopBorrowers from "./component/TopBorrowers";
import Carousel from "./component/Carousel";
import { authenticationService } from "@authentication/services/AuthenticationService";

const BASE_URL = process.env.REACT_APP_INVENIO_UI_URL;
const ADMIN_ROLES = ["admin", "librarian"];

// Custom hooks
const useUser = () => {
  const [user, setUser] = useState(null);
  const intervalRef = useRef(null);

  useEffect(() => {
    intervalRef.current = setInterval(() => {
      if (sessionManager.user) {
        setUser(sessionManager.user);
        clearInterval(intervalRef.current);
      }
    }, 300);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  return user;
};

const useMobileDetection = () => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 767);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return isMobile;
};

// Utility functions
const formatImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith("http")) return url;
  if (url.startsWith("/api/")) return `${BASE_URL}${url}`;
  if (url.startsWith("/")) return `${BASE_URL}${url}`;
  if (!url.includes("/")) return `${BASE_URL}/api/files/serve/${url}`;
  return `${BASE_URL}/${url}`;
};

// Hide features based on user roles
const canViewTopBorrowedBooks = (user) => {
  if (!user) return false;
  return !authenticationService.hasRoles(user, ["admin"]);
};

const canViewTopBorrowers = (user) => {
  if (!user) return false;
  return !authenticationService.hasRoles(user, ["admin"]);
};

const canViewCarousel = (user) => {
  if (!user) return false;
  return !authenticationService.hasRoles(user, ["admin"]);
};

const getCSRFToken = () => {
  const metaTag = document.querySelector('meta[name="csrf-token"]');
  if (metaTag) return metaTag.getAttribute("content");

  const csrfCookie = document.cookie
    .split("; ")
    .find(
      (row) => row.startsWith("csrftoken=") || row.startsWith("csrf_token=")
    );

  if (csrfCookie) return csrfCookie.split("=")[1];

  return (
    sessionStorage.getItem("csrf_token") ||
    localStorage.getItem("csrf_token") ||
    sessionStorage.getItem("csrftoken") ||
    localStorage.getItem("csrftoken")
  );
};

const getUserRoles = (user) => {
  if (!user) return [];

  let roles = [];

  if (user.roles && Array.isArray(user.roles) && user.roles.length > 0) {
    roles = [...user.roles];
  }

  if (user.userType && Array.isArray(user.userType)) {
    const userTypeRoles = user.userType.map((type) =>
      type.user_type?.toLowerCase()
    );
    roles = [...roles, ...userTypeRoles.filter(Boolean)];
  }

  if (roles.length === 0 && user) {
    roles = ["visitor"];
  }

  return roles;
};

const hasAdminRights = (user) => {
  const roles = getUserRoles(user);
  return roles.some((role) => ADMIN_ROLES.includes(role?.toLowerCase?.()));
};

const getInitialFormState = () => ({
  title: "",
  description: "",
  targetGroup: ["all"],
  image: null,
  date: new Date().toLocaleDateString(),
  isPinned: false,
  id: null,
});

const DashboardAdvisory = () => {
  const user = useUser();
  const isMobile = useMobileDetection();
  const isMountedRef = useRef(true);

  const [data, setData] = useState({ announcements: [] });
  const [modals, setModals] = useState({ addAnnouncement: false });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [editingAnnouncement, setEditingAnnouncement] = useState(null);
  const [forms, setForms] = useState({ announcement: getInitialFormState() });

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Process announcements data
  const processAnnouncements = (announcements) => {
    return announcements.map((announcement) => {
      const processedAnn = { ...announcement };
      const rawImageUrl =
        announcement.imageUrl ||
        announcement.image_url ||
        announcement.url ||
        announcement.image ||
        announcement.imagePath ||
        announcement.image_path ||
        announcement.attachmentUrl ||
        announcement.attachment_url;

      if (rawImageUrl) {
        const formattedUrl = formatImageUrl(rawImageUrl);
        processedAnn.imageUrl = formattedUrl;
        processedAnn.image_url = formattedUrl;
      }

      return processedAnn;
    });
  };

  const fetchAnnouncements = async () => {
    if (!isMountedRef.current) return;

    try {
      setLoading(true);
      setError(null);

      const response = await announcementApi.list();
      if (isMountedRef.current && response.data) {
        const announcements =
          response.data.data || response.data.hits || response.data || [];
        const processedAnnouncements = processAnnouncements(announcements);

        setData((prev) => ({ ...prev, announcements: processedAnnouncements }));
      }
    } catch (error) {
      if (isMountedRef.current) {
        setError("Failed to load announcements. Please try again.");
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    fetchAnnouncements();
  }, []);

  // Filter and sort announcements
  const getFilteredAnnouncements = () => {
    if (!user || !data.announcements) return [];
    const userRoles = getUserRoles(user);

    const userRoleTypes = userRoles
      .map((role) => role?.toLowerCase())
      .filter(Boolean);

    if (userRoleTypes.some((role) => ADMIN_ROLES.includes(role))) {
      return data.announcements;
    }

    return data.announcements.filter((announcement) => {
      const targetGroupStr =
        announcement.targetGroup || announcement.target_group || "all";
      const targetGroups = Array.isArray(targetGroupStr)
        ? targetGroupStr
        : targetGroupStr.split(",");

      if (targetGroups.includes("all")) return true;
      return userRoleTypes.some((userRole) =>
        targetGroups.map((g) => g.toLowerCase()).includes(userRole)
      );
    });
  };

  const getSortedAnnouncements = () => {
    const filtered = getFilteredAnnouncements();
    return filtered.sort((a, b) => {
      const aPinned = a.isPinned || a.is_pinned;
      const bPinned = b.isPinned || b.is_pinned;

      if (aPinned && !bPinned) return -1;
      if (!aPinned && bPinned) return 1;

      return (
        new Date(b.created_at || b.date) - new Date(a.created_at || a.date)
      );
    });
  };

  // Form management
  const clearForm = () => {
    setForms({ announcement: getInitialFormState() });
    setEditingAnnouncement(null);
  };

  const toggleModal = (name) => {
    if (!isMountedRef.current) return;

    setModals((m) => {
      const wasOpen = m[name];
      if (wasOpen) clearForm();
      return { ...m, [name]: !wasOpen };
    });
  };

  const openAddModal = () => {
    clearForm();
    setModals((m) => ({ ...m, addAnnouncement: true }));
  };

  const updateForm = (type, field, value) => {
    if (!isMountedRef.current) return;
    setForms((f) => ({ ...f, [type]: { ...f[type], [field]: value } }));
  };

  // Upload image helper
  const submitAnnouncement = async () => {
    if (!isMountedRef.current) return;

    try {
      setLoading(true);
      setError(null);

      const { title, description, targetGroup } = forms.announcement;
      if (!title?.trim()) throw new Error("Title is required");
      if (!description?.trim()) throw new Error("Description is required");
      if (
        !targetGroup ||
        (Array.isArray(targetGroup) && targetGroup.length === 0)
      ) {
        throw new Error("At least one target group is required");
      }

      const isEditing = editingAnnouncement !== null;

      const announcementData = {
        title: title.trim(),
        description: description.trim(),
        targetGroup,
        isPinned: forms.announcement.isPinned || false,
        fullName: user?.fullName || user?.name || user?.email || "Unknown User",
      };

      // Only handle image removal explicitly
      // Let the backend maintain the existing image URL if not changing
      if (isEditing && forms.announcement.removeImage) {
        announcementData.removeImage = true;
      }

      let response;
      if (isEditing) {
        response = await announcementApi.update(
          editingAnnouncement.id,
          announcementData
        );
      } else {
        response = await announcementApi.create(announcementData);
      }

      // Handle image upload if there's a NEW image file
      if (forms.announcement.image && response.data) {
        try {
          const imageFormData = new FormData();
          imageFormData.append("file", forms.announcement.image);

          const csrfToken = getCSRFToken();
          if (csrfToken) {
            imageFormData.append("csrf_token", csrfToken);
          }

          const imageResponse = await fetch(
            `${process.env.REACT_APP_INVENIO_REST_ENDPOINTS_BASE_URL}/announcements/${response.data.id}/upload-image`,
            {
              method: "POST",
              body: imageFormData,
              credentials: "include",
              headers: {
                ...(csrfToken && { "X-CSRFToken": csrfToken }),
              },
            }
          );

          if (imageResponse.ok) {
            const imageResult = await imageResponse.json();
            const rawImageUrl =
              imageResult.imageUrl ||
              imageResult.url ||
              imageResult.image_url ||
              imageResult.data?.imageUrl ||
              imageResult.data?.url ||
              imageResult.data?.image_url;

            if (rawImageUrl) {
              const formattedUrl = formatImageUrl(rawImageUrl);
              response.data.imageUrl = formattedUrl;
              response.data.image_url = formattedUrl;
            }
          } else {
            throw new Error("Image upload failed");
          }
        } catch (imageError) {
          if (isMountedRef.current) {
            setError(
              "Announcement saved but image upload failed. You can edit the announcement to add an image later."
            );
          }
        }
      } else if (
        isEditing &&
        !forms.announcement.image &&
        !forms.announcement.removeImage
      ) {
        // Preserve existing image when editing without new image
        // Check if backend returned the imageUrl, if not, use the one from editingAnnouncement
        if (!response.data.imageUrl && !response.data.image_url) {
          const existingImageUrl =
            editingAnnouncement.imageUrl || editingAnnouncement.image_url;

          if (existingImageUrl) {
            response.data.imageUrl = existingImageUrl;
            response.data.image_url = existingImageUrl;
          }
        }
      } else if (forms.announcement.removeImage) {
        // Explicitly clear image URLs when user wants to remove
        response.data.imageUrl = null;
        response.data.image_url = null;
      }

      if (response.data && isMountedRef.current) {
        // Process the announcement
        const processedAnnouncement = {
          ...response.data,
          fullName:
            user?.fullName || user?.name || user?.email || "Unknown User",
          date: response.data.created_at || new Date().toLocaleDateString(),
          created_at: response.data.created_at || new Date().toISOString(),
        };

        // Apply image URL processing only if not explicitly removed
        if (!forms.announcement.removeImage) {
          const rawImageUrl =
            processedAnnouncement.imageUrl ||
            processedAnnouncement.image_url ||
            processedAnnouncement.url ||
            processedAnnouncement.image ||
            processedAnnouncement.imagePath ||
            processedAnnouncement.image_path ||
            processedAnnouncement.attachmentUrl ||
            processedAnnouncement.attachment_url;

          if (rawImageUrl) {
            const formattedUrl = formatImageUrl(rawImageUrl);
            processedAnnouncement.imageUrl = formattedUrl;
            processedAnnouncement.image_url = formattedUrl;
          }
        } else {
          // Explicitly remove image URLs if removeImage flag is set
          processedAnnouncement.imageUrl = null;
          processedAnnouncement.image_url = null;
        }

        if (isEditing) {
          setData((prev) => ({
            ...prev,
            announcements: prev.announcements.map((ann) =>
              ann.id === editingAnnouncement.id ? processedAnnouncement : ann
            ),
          }));
        } else {
          setData((prev) => ({
            ...prev,
            announcements: [processedAnnouncement, ...prev.announcements],
          }));
        }

        clearForm();
        toggleModal("addAnnouncement");
      }
    } catch (error) {
      let errorMessage = "Failed to save announcement. Please try again.";

      if (error.response?.status === 400) {
        const errorData = error.response.data;
        if (errorData.errors) {
          const errorMessages = Object.values(errorData.errors).flat();
          errorMessage = `Validation error: ${errorMessages.join(", ")}`;
        } else if (errorData.message) {
          errorMessage = errorData.message;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }

      if (isMountedRef.current) {
        setError(errorMessage);
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  };

  const deleteAnnouncement = async (id) => {
    if (!isMountedRef.current) return;

    try {
      setLoading(true);
      setError(null);

      const response = await announcementApi.delete(id);

      if (response.data?.status === "success" && isMountedRef.current) {
        setData((prev) => ({
          ...prev,
          announcements: prev.announcements.filter((a) => a.id !== id),
        }));
      }
    } catch (error) {
      if (isMountedRef.current) {
        setError("Failed to delete announcement. Please try again.");
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  };

  const editAnnouncement = (item) => {
    if (!isMountedRef.current) return;

    setEditingAnnouncement(item);

    let targetGroup = item.targetGroup || item.target_group || "all";
    if (typeof targetGroup === "string") {
      targetGroup = targetGroup.split(",");
    }

    const formData = {
      id: item.id,
      title: item.title || "",
      description: item.description || "",
      targetGroup: targetGroup,
      image: null,
      existingImageUrl: item.imageUrl || item.image_url || null,
      date: item.date || item.created_at || new Date().toLocaleDateString(),
      isPinned: item.isPinned || item.is_pinned || false,
    };

    setForms((f) => ({ ...f, announcement: formData }));
    setModals((m) => ({ ...m, addAnnouncement: true }));
  };

  const togglePinAnnouncement = async (id) => {
    if (!isMountedRef.current) return;

    const currentAnnouncement = data.announcements.find((a) => a.id === id);
    if (!currentAnnouncement) return;

    const currentPinStatus =
      currentAnnouncement.isPinned || currentAnnouncement.is_pinned || false;
    const newPinStatus = !currentPinStatus;

    setData((prev) => ({
      ...prev,
      announcements: prev.announcements.map((a) =>
        a.id === id
          ? { ...a, isPinned: newPinStatus, is_pinned: newPinStatus }
          : a
      ),
    }));

    try {
      setLoading(true);
      setError(null);
      await announcementApi.togglePin(id);
    } catch (error) {
      if (isMountedRef.current) {
        setData((prev) => ({
          ...prev,
          announcements: prev.announcements.map((a) =>
            a.id === id
              ? {
                  ...a,
                  isPinned: currentPinStatus,
                  is_pinned: currentPinStatus,
                }
              : a
          ),
        }));
        setError("Failed to update pin status. Please try again.");
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  };

  const canEdit = hasAdminRights(user);
  const sortedAnnouncements = getSortedAnnouncements();

  const headerStyle = {
    width: "100%",
    background: "#ffffffff",
    borderRadius: "12px",
    padding: isMobile ? "0.75em 1em" : "1em 1.5em",
    boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
    border: "1px solid #e5e5e5",
    display: "flex",
    flexDirection: isMobile ? "column" : "row",
    alignItems: isMobile ? "flex-start" : "center",
    justifyContent: "space-between",
    gap: "0.5em",
  };

  const segmentStyle = {
    width: "100%",
    maxWidth: "1000px",
    borderRadius: "16px",
    display: "flex",
    flexDirection: "column",
    maxHeight: isMobile ? "none" : "300vh",
    background: "rgba(255, 255, 255, 0)",
    padding: isMobile ? "0.5rem" : "1rem",
  };

  const contentStyle = {
    overflowY: isMobile ? "visible" : "auto",
    scrollbarWidth: "none",
    flexGrow: 1,
    padding: isMobile ? "0.5em" : "1em",
    background: "transparent",
    borderRadius: "12px",
  };

  return (
    <Container
      fluid
      className="dashboard-advisory-container"
      style={{
        padding: "1em",
        paddingTop: "3em",
        display: "flex",
        justifyContent: "center",
      }}
    >
      <Grid
        columns={3}
        stackable
        className="dashboard-advisory-grid"
        style={{ width: "100%", maxWidth: "1400px" }}
      >
        {canViewCarousel(user) && (
          <Grid.Row>
            <Grid.Column width={16}>
              <Carousel isMobile={isMobile} />
            </Grid.Column>
          </Grid.Row>
        )}

        <Grid.Row>
          {/* Top Borrowed Books - Left sidebar on desktop, hidden on mobile (shown below) */}
          {!isMobile && (
            <Grid.Column computer={4} tablet={16} mobile={16}>
              {canViewTopBorrowedBooks(user) && <TopBorrowedBooks />}
            </Grid.Column>
          )}

          {/* Library Announcements - Center on desktop, stacked on mobile */}
          <Grid.Column
            computer={8}
            tablet={16}
            mobile={16}
            className="dashboard-advisory-content"
            style={{ display: "flex", justifyContent: "center" }}
            data-tour="library-announcements"
          >
            <Segment raised style={segmentStyle}>
              <div style={headerStyle}>
                <Header
                  as="h3"
                  color="green"
                  style={{
                    fontWeight: 700,
                    fontSize: isMobile ? "1.2em" : "1.5em",
                    display: "flex",
                    alignItems: "center",
                    gap: "0.5em",
                    margin: 0,
                  }}
                >
                  <Icon
                    name="bullhorn"
                    size={isMobile ? "large" : "big"}
                    color="green"
                  />
                  Library Announcements
                </Header>

                {canEdit && (
                  <Button
                    color="green"
                    size={isMobile ? "small" : "medium"}
                    onClick={openAddModal}
                    disabled={loading}
                    style={{
                      borderRadius: "8px",
                      fontWeight: 600,
                      padding: isMobile ? "0.5em 1em" : "0.75em 1.25em",
                      fontSize: isMobile ? "0.9em" : "1em",
                      transition: "background 0.3s, transform 0.2s",
                    }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.transform = "scale(1.02)")
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.transform = "scale(1)")
                    }
                  >
                    <Icon name="plus" /> Post Announcement
                  </Button>
                )}
              </div>

              {error && (
                <Message negative onDismiss={() => setError(null)}>
                  {error}
                </Message>
              )}

              <div style={contentStyle}>
                {loading && sortedAnnouncements.length === 0 ? (
                  <div style={{ textAlign: "center", padding: "2em" }}>
                    <Icon name="spinner" loading size="big" color="green" />
                    <p>Loading announcements...</p>
                  </div>
                ) : sortedAnnouncements.length === 0 ? (
                  <p
                    style={{
                      color: "white",
                      fontStyle: "italic",
                      textAlign: "center",
                      fontSize: isMobile ? "0.95em" : "1.05em",
                    }}
                  >
                    You're all caught up! No announcements at the moment.
                  </p>
                ) : (
                  sortedAnnouncements.map((item) => (
                    <Post
                      key={item.id}
                      item={item}
                      canEdit={
                        canEdit &&
                        (hasAdminRights(user) ||
                          item.postedById === user?.id ||
                          item.postedById === user?.username)
                      }
                      onEdit={editAnnouncement}
                      onDelete={deleteAnnouncement}
                      onPin={togglePinAnnouncement}
                    />
                  ))
                )}
              </div>
            </Segment>
          </Grid.Column>

          {/* Top Borrowers - Right sidebar on desktop, stacked on mobile */}
          <Grid.Column computer={4} tablet={16} mobile={16}>
            {canViewTopBorrowers(user) && <TopBorrowers />}
          </Grid.Column>
        </Grid.Row>

        {/* Top Borrowed Books - Only shown on mobile, placed after announcements */}
        {isMobile && canViewTopBorrowedBooks(user) && (
          <Grid.Row>
            <Grid.Column width={16}>
              <TopBorrowedBooks />
            </Grid.Column>
          </Grid.Row>
        )}
      </Grid>

      <PostModal
        open={modals.addAnnouncement || false}
        onClose={() => toggleModal("addAnnouncement")}
        onSubmit={submitAnnouncement}
        formData={{
          ...forms.announcement,
          targetGroup: Array.isArray(forms.announcement.targetGroup)
            ? forms.announcement.targetGroup.join(", ")
            : forms.announcement.targetGroup,
        }}
        onChange={(field, value) => updateForm("announcement", field, value)}
        currentUser={user}
        loading={loading}
        isEditing={editingAnnouncement !== null}
      />
    </Container>
  );
};

export default DashboardAdvisory;
